import { styled, useTheme } from '@mui/material';
import ButtonBase, { ButtonBaseProps } from '@mui/material/ButtonBase';

import { Sizes, WithRef } from '../../types';
import { Spinner } from '../feedback';
import { IconContext } from '../icons';

type IconButtonSize = Sizes<'sm' | 'md' | 'lg'>;

export type IconButtonProps = Omit<
  ButtonBaseProps<
    'button',
    {
      loading?: boolean;
      variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
      size?: IconButtonSize;
      testId?: string;
    }
  >,
  'css'
>;

const IconButton = styled(
  ({ ref, testId, ...props }: WithRef<HTMLButtonElement, IconButtonProps>) => {
    const theme = useTheme();
    const _props = {
      ...(theme.components?.DsIconButton?.defaultProps ?? {}),
      ...props,
    };
    const { children, loading, disabled, ...rest } = _props;

    return (
      <IconContext.Provider
        value={{
          size: '1em',
          weight: props.size === 'sm' ? 'regular' : 'bold',
        }}
      >
        <ButtonBase
          ref={ref}
          data-testid={testId}
          {...rest}
          disabled={disabled || loading}
        >
          {loading ? (
            <span className="DsIconButton-Spinner">
              <Spinner size={props.size === 'sm' ? '16px' : '20px'} />
            </span>
          ) : (
            children
          )}
        </ButtonBase>
      </IconContext.Provider>
    );
  },
  {
    name: 'DsIconButton',
    slot: 'Root',
    target: 'DsIconButton-root',
    overridesResolver: ({ variant, size, disabled, loading }, theme) => {
      return [
        theme.root,
        theme[variant],
        theme[size],
        disabled || loading ? theme.disabled : {},
        loading ? theme.loading : {},
      ];
    },
  }
)<IconButtonProps>``;

IconButton.displayName = 'IconButton';

export default IconButton;
